import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/errors";
import { CheckCircle2, XCircle, Bot, User as UserIcon, Sparkles, Flame } from "lucide-react";
import SponsorBadge from "@/components/SponsorBadge";
import { ShareButton } from "@/components/ShareCard";

export default function ChallengeDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [error, setError] = useState("");
  const [voting, setVoting] = useState(false);

  const load = () => {
    api.get(`/challenges/${id}`)
      .then((r) => setC(r.data))
      .catch((e) => setError(formatApiError(e)));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const onVote = async (label) => {
    if (!user) { nav("/login?from=/challenges/" + id); return; }
    if (c.user_vote || voting) return;
    setVoting(true);
    setError("");
    try {
      const { data } = await api.post(`/challenges/${id}/vote`, { vote: label });
      setC(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setVoting(false);
    }
  };

  if (!c) {
    return <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-20 label-mono">{error || "Loading…"}</div>;
  }

  const revealed = !!c.user_vote;
  const total = (c.votes_a || 0) + (c.votes_b || 0);
  const pctA = total ? Math.round((c.votes_a / total) * 100) : 50;
  const pctB = 100 - pctA;

  const renderOption = (opt, side) => {
    const isAI = revealed && opt.source === "ai";
    const isHuman = revealed && opt.source === "human";
    const userPicked = c.user_vote === opt.label;
    const userCorrect = userPicked && c.user_correct;
    const userWrong = userPicked && !c.user_correct;
    const isImage = c.type === "image" && opt.content?.startsWith("data:image");

    return (
      <div
        key={opt.label}
        className={`opt-panel ${revealed ? "locked" : ""}`}
        onClick={() => !revealed && onVote(opt.label)}
        data-testid={`option-${opt.label}`}
        style={revealed ? {
          background: isAI ? "rgba(37,99,235,0.06)" : "rgba(220,38,38,0.06)",
        } : {}}
      >
        <span className="opt-letter">{opt.label}</span>
        <div className="flex items-center justify-between mb-5">
          <div className="label-mono">Option {opt.label}</div>
          {revealed && (
            <div className={`tag flex items-center gap-1.5 ${isAI ? "tag-ai" : "tag-human"}`}>
              {isAI ? <Bot size={12} /> : <UserIcon size={12} />}
              {isAI ? "AI Generated" : "Human Created"}
            </div>
          )}
        </div>

        {isImage ? (
          <div className="media-frame mb-4">
            <img src={opt.content} alt={`Option ${opt.label}`} className="w-full h-72 object-cover" />
          </div>
        ) : (
          <div className="text-xl md:text-2xl leading-snug font-medium pr-12 mb-6 min-h-[120px]">
            {opt.content}
          </div>
        )}

        {revealed && opt.note && (
          <div className="label-mono mb-4" style={{ color: "var(--text-dim)" }}>
            {opt.note}
          </div>
        )}

        {revealed && (
          <div className="mt-4">
            <div className="flex items-center justify-between label-mono mb-2">
              <span>{side === "A" ? `${pctA}%` : `${pctB}%`} chose this</span>
              <span>{side === "A" ? c.votes_a : c.votes_b} votes</span>
            </div>
            <div className="reveal-bar">
              <span style={{
                background: isAI ? "var(--ai)" : "var(--human)",
                transform: `scaleX(${(side === "A" ? pctA : pctB) / 100})`,
              }} />
            </div>
          </div>
        )}

        {userPicked && (
          <div className={`mt-4 flex items-center gap-2 label-mono ${userCorrect ? "tag-correct" : "tag-wrong"} px-3 py-2 inline-flex`}>
            {userCorrect ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
            {userCorrect ? "+10 pts" : "Better luck next time"}
          </div>
        )}

        {!revealed && (
          <button
            className="btn btn-secondary w-full mt-2"
            onClick={(e) => { e.stopPropagation(); onVote(opt.label); }}
            disabled={voting}
            data-testid={`vote-option-${opt.label}-button`}
          >
            This one is AI →
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-16" data-testid="challenge-detail">
      <Link to="/challenges" className="label-mono hover:text-white" data-testid="back-to-challenges">← All episodes</Link>
      <div className="mt-6 mb-3 flex items-center gap-3 flex-wrap">
        <span className="tag">{c.category}</span>
        <span className="tag">{c.type}</span>
        {c.sponsor && <SponsorBadge sponsor={c.sponsor} />}
        {revealed && (
          c.user_correct
            ? <span className="tag tag-correct flex items-center gap-1"><CheckCircle2 size={12}/> You got it</span>
            : <span className="tag tag-wrong flex items-center gap-1"><XCircle size={12}/> Missed it</span>
        )}
      </div>
      <h1 className="font-display font-black tracking-tighter uppercase text-4xl md:text-6xl leading-[0.95]" data-testid="challenge-title">
        {c.title}
      </h1>
      {c.hook && (
        <p className="text-lg md:text-xl mt-6 max-w-3xl" style={{ color: "var(--text-dim)" }}>
          {c.hook}
        </p>
      )}

      <div className="mt-10 flex flex-wrap gap-3 label-mono">
        <span><Sparkles size={12} className="inline mr-1.5"/> Click an option you think is AI</span>
        {!user && <span>· <Link to="/login" className="underline">Login</Link> to vote</span>}
      </div>

      {error && <div className="mt-6 tag tag-wrong" data-testid="vote-error">{error}</div>}

      <div className="mt-8 split-grid">
        {renderOption(c.option_a, "A")}
        {renderOption(c.option_b, "B")}
      </div>

      {revealed && c.streak && c.streak.streak_changed && (
        <div className="card p-6 mt-6 slide-up flex items-center gap-4" style={{ borderColor: "rgba(251,146,60,0.4)", background: "rgba(251,146,60,0.06)" }} data-testid="streak-toast">
          <Flame size={28} style={{ color: "#fb923c" }} className="pulse-glow" />
          <div className="flex-1">
            <div className="font-display font-bold text-lg">
              Day {c.streak.new_streak} streak{c.streak.bonus ? ` · +${c.streak.bonus} bonus points!` : ""}
            </div>
            <div className="text-sm" style={{ color: "var(--text-dim)" }}>
              {c.streak.bonus
                ? "Milestone unlocked. Keep the chain alive."
                : "Come back tomorrow for the next Daily Challenge."}
            </div>
          </div>
        </div>
      )}

      {revealed && c.explanation && (
        <div className="card p-8 mt-8 slide-up" data-testid="explanation-section">
          <div className="label-mono mb-3" style={{ color: "var(--success)" }}>The Reveal</div>
          <h3 className="font-display font-bold text-2xl mb-3 tracking-tight">
            Why {c.ai_label === "A" ? "Option B" : "Option A"} won
          </h3>
          <p style={{ color: "var(--text-dim)" }} className="text-lg leading-relaxed">
            {c.explanation}
          </p>
        </div>
      )}

      <div className="mt-10 flex gap-3 flex-wrap">
        <Link to="/challenges" className="btn btn-secondary" data-testid="next-episode-button">
          Next episode →
        </Link>
        {revealed && user && <ShareButton testId="challenge-share-button" />}
        {revealed && (
          <Link to="/store" className="btn btn-ai" data-testid="post-vote-store-cta">
            <Sparkles size={14} /> Get the templates behind the winners
          </Link>
        )}
      </div>
    </div>
  );
}
